import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiArrowRight,
  FiCheck,
  FiAlertCircle,
  FiUser,
  FiLock,
  FiPhone,
  FiUsers,
  FiEye,
  FiEyeOff
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logo.png";

const colors = {
  brightBlue: '#00A9EC',
  deepOrange: '#E06207',
  nearBlack: '#0E1306',
  green: '#018F2C',
  darkBrown: '#602005',
  burntOrange: '#A65007',
  lightOrange: '#EF9222',
  darkGreen: '#056619',
  reddishBrown: '#9C2E04',
  darkBlue: '#0067A1',
  skyBlue: '#0DC8F8',
  peachOrange: '#FDA653',
  navyBlue: '#023057',
  oliveBrown: '#604B0C',
  tealBlue: '#024D6E',
  pinkMagenta: '#A70756',
  goldenYellow: '#F3D71B',
  freshGreen: '#12B14A',
  lightGreen: '#55CB71',
  lightCyan: '#56E0F9'
};

const RegisterStep1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralMessage, setReferralMessage] = useState("");
  const [sponsorName, setSponsorName] = useState("");
  const [createdUser, setCreatedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    dateOfBirth: "",
    password: "",
    confirmPassword: "",
    sponsorId: "",
    userType: "member"
  });

  useEffect(() => {
    const validateReferralFromUrl = async () => {
      const rawQuery = (location.search || "").replace(/^\?/, "").trim();
      if (!rawQuery) {
        setReferralValid(false);
        setReferralMessage("Registration is available only through a valid sponsor link.");
        setSponsorName("");
        setFormData((prev) => ({
          ...prev,
          sponsorId: "",
        }));
        return;
      }

      let referralCode = "";

      if (rawQuery.includes("=")) {
        const params = new URLSearchParams(location.search);
        referralCode =
          params.get("referralCode") ||
          params.get("ReferralCode") ||
          params.get("code") ||
          "";

        if (!referralCode) {
          const firstKey = params.keys().next().value;
          if (firstKey && !params.get(firstKey)) {
            referralCode = firstKey;
          }
        }
      } else {
        referralCode = rawQuery;
      }

      referralCode = decodeURIComponent(referralCode).trim().toUpperCase();
      if (!referralCode) return;

      setReferralLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/validate-referral/${referralCode}`
        );

        if (response.data?.exists) {
          setFormData((prev) => ({
            ...prev,
            sponsorId: response.data.sponsorUniqueId || response.data.partnerId || referralCode,
          }));
          setReferralValid(true);
          setSponsorName(response.data.sponsorName || "");
          const typeLabel =
            response.data.partnerType === "lbbp"
              ? "LBBP"
              : response.data.partnerType === "puap"
                ? "PUAP"
                : response.data.partnerType === "both"
                  ? "LBBP + PUAP"
                  : "Member";
          setReferralMessage(`${typeLabel} Partner verified: ${response.data.sponsorName}`);
        } else {
          setReferralValid(false);
          setSponsorName("");
          setFormData((prev) => ({
            ...prev,
            sponsorId: "",
          }));
          setReferralMessage("Invalid sponsor link. Please register with a valid sponsor link.");
        }
      } catch (err) {
        setReferralValid(false);
        setSponsorName("");
        setFormData((prev) => ({
          ...prev,
          sponsorId: "",
        }));
        setReferralMessage("Invalid sponsor link. Please register with a valid sponsor link.");
      } finally {
        setReferralLoading(false);
      }
    };

    validateReferralFromUrl();
  }, [location.search]);

  // Calculate password strength
  useEffect(() => {
    const password = formData.password;
    let strength = 0;

    if (password.length >= 8) strength += 25;
    if (password.match(/[a-z]+/)) strength += 25;
    if (password.match(/[A-Z]+/)) strength += 25;
    if (password.match(/[0-9]+/)) strength += 25;
    if (password.match(/[$@#&!]+/)) strength += 25;

    setPasswordStrength(Math.min(strength, 100));
  }, [formData.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError("");
  };

  const validateForm = () => {
    if (referralValid !== true || !formData.sponsorId) return "Valid sponsor link is required for registration";
    if (!formData.name) return "Name is required";
    if (!formData.mobile) return "Mobile is required";
    if (!/^\d{10}$/.test(formData.mobile)) return "Mobile must be 10 digits";
    if (!formData.dateOfBirth) return "Date of birth is required";
    if (!formData.password) return "Password is required";
    if (formData.password.length < 8) return "Password must be at least 8 characters";
    if (formData.password !== formData.confirmPassword) return "Passwords do not match";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/register-step1`,
        {
          name: formData.name,
          mobile: formData.mobile,
          dateOfBirth: formData.dateOfBirth,
          password: formData.password,
          userType: "member",
          sponsorId: formData.sponsorId?.trim().toUpperCase() || "",
        }
      );

      setSuccess(response.data.message);
      setCreatedUser({
        name: response.data?.user?.name || formData.name,
        uniqueId: response.data?.user?.uniqueId,
        password: formData.password,
        sponsorName: response.data?.user?.sponsorName || "N/A",
        sponsorReferralId:
          response.data?.user?.sponsorUniqueId ||
          response.data?.user?.sponsorReferralId ||
          response.data?.user?.sponsorId ||
          "N/A",
      });

      localStorage.removeItem("registrationUserId");
      localStorage.removeItem("currentStep");
      localStorage.removeItem("userEmail");

    } catch (err) {
      setError(err.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 50) return "bg-red-500";
    if (passwordStrength < 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 50) return "Weak";
    if (passwordStrength < 75) return "Medium";
    return "Strong";
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 px-3 py-4">

      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

        {/* Grid Pattern */}
        <div className={`absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20`}></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-5xl"
      >

        {/* Glass Card - Compact */}
        <div className="bg-white/10 backdrop-blur-xl shadow-2xl rounded-2xl p-5 md:p-6 border border-white/20">

          {/* Logo and Title - Compact */}
          <div className="text-center mb-4">
            <img 
              src={logo} 
              alt="ABM Logo" 
              className="w-20 h-20 rounded-full object-contain mx-auto mb-3"
            />
            {/* ABM letter boxes commented out */}
            {/* <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: colors.pinkMagenta, color: "blue" }}>A</div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg"
                style={{ backgroundColor: colors.freshGreen, color: "blue" }}>B</div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: colors.goldenYellow, color: "blue" }}>M</div>
            </div> */}

            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold text-white mb-0.5"
            >
              Create Account
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-white/60 text-xs"
            >
              Join ABM GROUP One Network today
            </motion.p>
          </div>

          {/* Alert Messages - Compact */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 p-2.5 rounded-lg bg-red-500/20 border border-red-500/30 flex gap-2 backdrop-blur-sm text-sm"
              >
                <FiAlertCircle className="text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-red-200 text-xs">{error}</p>
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-3 p-2.5 rounded-lg bg-green-500/20 border border-green-500/30 flex gap-2 backdrop-blur-sm text-sm"
              >
                <FiCheck className="text-green-400 mt-0.5 flex-shrink-0" />
                <p className="text-green-200 text-xs">{success}</p>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Personal Information - Compact */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-white/90 border-b border-white/10 pb-1.5 flex items-center gap-1.5">
                <FiUser className="text-purple-400 text-xs" />
                Personal Information
              </h3>

              <div className="space-y-2">
                {/* Name Input */}
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 text-sm" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full Name"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm"
                  />
                </div>

                {/* Mobile Input */}
                <div className="relative">
                  <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 text-sm" />
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Mobile Number"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm"
                  />
                </div>

                {/* Date of Birth Input */}
                <div className="relative">
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    placeholder="Date of Birth"
                    className="w-full pl-3 pr-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm"
                  />
                </div>

                {/* Password Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="relative">
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 text-sm" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Password"
                      className="w-full pl-9 pr-8 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60"
                    >
                      {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </button>
                  </div>

                  <div className="relative">
                    <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 text-sm" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm Password"
                      className="w-full pl-9 pr-8 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60"
                    >
                      {showConfirmPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </button>
                  </div>
                </div>

                {/* Password Strength Indicator - Compact */}
                {formData.password && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-1"
                  >
                    <div className="flex justify-between text-xs">
                      <span className="text-white/60">Password Strength</span>
                      <span className={`font-semibold text-xs ${passwordStrength < 50 ? 'text-red-400' :
                          passwordStrength < 75 ? 'text-yellow-400' : 'text-green-400'
                        }`}>
                        {getPasswordStrengthText()}
                      </span>
                    </div>
                    <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${passwordStrength}%` }}
                        className={`h-full ${getPasswordStrengthColor()} rounded-full`}
                      />
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Reference Section - Compact */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-white/90 border-b border-white/10 pb-1.5 flex items-center gap-1.5">
                <FiUsers className="text-purple-400 text-xs" />
                Reference
              </h3>

              {/* Referral Messages - Compact */}
              <AnimatePresence>
                {referralLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="p-2 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-200 text-xs backdrop-blur-sm"
                  >
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-400 border-t-transparent"></div>
                      Checking referral code...
                    </div>
                  </motion.div>
                )}

                {!referralLoading && referralValid === false && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="p-2 rounded-lg bg-yellow-500/20 border border-yellow-500/30 text-yellow-200 text-xs backdrop-blur-sm flex items-center gap-2"
                  >
                    <FiAlertCircle className="flex-shrink-0 text-xs" />
                    {referralMessage}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Sponsor ID Input - Compact */}
              <div className="relative">
                <FiUsers className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 text-sm" />
                <input
                  type="text"
                  name="sponsorId"
                  value={formData.sponsorId}
                  readOnly
                  placeholder="Sponsor ID from referral link"
                  disabled
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed text-sm"
                />
              </div>

              {formData.sponsorId && sponsorName && (
                <div className="rounded-lg bg-white/10 border border-white/10 px-3 py-1.5 text-xs text-white/80 backdrop-blur-sm">
                  Sponsor Name: <span className="font-semibold text-cyan-300">{sponsorName}</span>
                </div>
              )}
            </div>

            {/* Submit Button - Compact */}
            <motion.button
              type="submit"
              disabled={loading || referralLoading || referralValid !== true}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed relative overflow-hidden group text-sm"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account <FiArrowRight className="group-hover:translate-x-1 transition-transform text-sm" />
                  </>
                )}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </motion.button>

            {/* Login Link - Compact */}
            <p className="text-center text-white/60 text-xs">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="font-semibold text-purple-400 hover:text-purple-300 hover:underline transition-colors"
              >
                Login here
              </button>
            </p>

          </form>
        </div>
      </motion.div>

      {/* Success Modal - Compact */}
      <AnimatePresence>
        {createdUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="w-full max-w-sm rounded-xl p-4 text-center shadow-2xl bg-gradient-to-br from-gray-900 to-purple-900 border border-purple-500/30"
            >
              {/* Success Animation */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-10 h-10 rounded-full bg-gradient-to-r from-green-400 to-green-600 mx-auto mb-2 flex items-center justify-center"
              >
                <FiCheck className="text-white text-lg" />
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400 mb-0.5"
              >
                Congratulations!
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-white/70 text-xs mb-0.5"
              >
                Your registration was successful
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-xs font-semibold text-yellow-300 mb-2"
              >
                Welcome to ABM GROUP One Network
              </motion.p>

              {/* Sponsor Info */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-white/10 rounded-lg px-3 py-2 mb-2 text-left space-y-0.5 backdrop-blur-sm border border-white/10"
              >
                <p className="text-white/70 text-xs">
                  Sponsor Name: <span className="text-cyan-300 font-semibold">{createdUser.sponsorName}</span>
                </p>
                <p className="text-white/70 text-xs">
                  Sponsor ID: <span className="text-cyan-300 font-semibold">{createdUser.sponsorReferralId}</span>
                </p>
              </motion.div>

              {/* User Credentials */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-white/10 rounded-lg px-3 py-2 mb-2 text-left space-y-0.5 backdrop-blur-sm border border-white/10"
              >
                <p className="text-white/70 text-xs">
                  Name: <span className="text-yellow-300 font-semibold">{createdUser.name}</span>
                </p>
                <p className="text-white/70 text-xs">
                  User ID: <span className="text-cyan-300 font-semibold">{createdUser.uniqueId}</span>
                </p>
                <p className="text-white/70 text-xs">
                  Password: <span className="text-cyan-300 font-semibold">{createdUser.password}</span>
                </p>
              </motion.div>

              {/* Note */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-yellow-500/20 rounded-lg px-3 py-1.5 mb-3 border border-yellow-500/30"
              >
                <p className="text-yellow-200 text-[10px]">
                  <span className="font-semibold text-red-400">Note:</span> Save these credentials. They won't be shown again.
                </p>
              </motion.div>

              {/* Login Button */}
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                type="button"
                onClick={() => {
                  setCreatedUser(null);
                  navigate("/login");
                }}
                className="w-full py-2 rounded-lg font-semibold text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl text-sm"
              >
                Proceed to Login
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default RegisterStep1;